import Box from '@mui/material/Box';
import { type JSX, useEffect, useMemo, useState } from 'react';
import { generatePath, useLocation, useParams } from 'react-router';

import { ActionsApi } from 'Api/Actions/ActionsApi';
import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { ControlsApi } from 'Api/Controls/ControlsApi';
import { RiskRegisterApi } from 'Api/RiskRegister/RiskRegisterApi';
import { Accordion } from 'Components/Accordion/Accordion';
import { AccordionCollapse } from 'Components/Accordion/AccordionCollapse/AccordionCollapse';
import { AccordionToggle } from 'Components/Accordion/AccordionToggle/AccordionToggle';
import { Button, Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FormFieldMultiOptionSelect } from 'Components/FormField/FormFieldMultiOptionSelect/FormFieldMultiOptionSelect';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldTextAreaAi } from 'Components/FormField/FormFieldTextAreaAi/FormFieldTextAreaAi';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { MultipleActionMapping, MultipleActionMappingProps } from 'Components/MultipleActionMapping/MultipleActionMapping';
import { MultipleControlMapping, MultipleControlMappingProps } from 'Components/MultipleControlMapping/MultipleControlMapping';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { LinkButtonToast, TextToast } from 'Components/Toast/Toast';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_DELETE_REMOVE } from 'Config/Icons';
import { CREATE_ACTION, RISK_DETAILS, RISK_LISTING } from 'Config/PathTemplates';
import { REFERENCE } from 'Config/Tooltips';
import { controlTextToString, getFrameworkGroupControlParts, getHumanReadableControlIdFromControl } from 'Helpers/ControlFormatter/ControlFormatter';
import { getFrameworkGroupControlURL } from 'Helpers/URLBuilder/URLBuilder';
import { RISK_ROLES } from 'Helpers/UserUtils';
import { useAiContentAdvisor } from 'Hooks/AiContentAdvisor';
import { useControlMappingItems } from 'Hooks/ControlMapping';
import { Action, ActionStatus } from 'Models/Actions';
import { AiContentAdvisorFeature } from 'Models/ArtificialIntelligence';
import { ValidationError } from 'Models/ErrorTypes';
import { OperationalControl } from 'Models/OperationalControls';
import { RiskCategoryResponse, RiskResponse, RiskStrategy, SaveRiskRequest, WARNING_MESSAGE_INCOMPLETE_CONTROL_DISTRIBUTION } from 'Models/RiskRegister';
import { GroupOptionType, OptionType } from 'Models/Types/GlobalType';
import { UserResponse } from 'Models/User';

import { ControlDistribution } from './ControlDistribution/ControlDistribution';
import styles from './ManageRisk.module.css';

enum SubmitState {
    None,
    Saving,
    Saved,
    Deleted,
}

enum Modal {
    Delete,
    None,
}

// Allows filtering Actions based on their status in the "Map Actions" section of th page.
export enum ActionStatusFilterValues {
    HIDE_CLOSED_ACTIONS = 'Hide Closed Actions',
    SHOW_CLOSED_ACTIONS = 'Show Closed Actions',
}
const ActionStatusFilterOptions: OptionType[] = [
    {
        value: ActionStatusFilterValues.HIDE_CLOSED_ACTIONS,
        label: ActionStatusFilterValues.HIDE_CLOSED_ACTIONS,
    },
    {
        value: ActionStatusFilterValues.SHOW_CLOSED_ACTIONS,
        label: ActionStatusFilterValues.SHOW_CLOSED_ACTIONS,
    },
];

interface UrlParams {
    risk_id: string;
}

export enum RiskPageType {
    CREATE_NEW,
    MANAGE,
}

export interface SelectedControl {
    identifier: string;
    weight: number;
    name: string;
    description: string;
}

const selectedControlFromOperationalControl = (control: OperationalControl, weight: number): SelectedControl => {
    return {
        identifier: control.identifier,
        weight,
        name: getHumanReadableControlIdFromControl(control),
        description: controlTextToString(control.metadata.control_text),
    };
};

export interface ManageRiskProps {
    actionsApi: ActionsApi;
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    controlsApi: ControlsApi;
    riskRegisterApi: RiskRegisterApi;
    pageType: RiskPageType;
}

export const ManageRisk = (props: ManageRiskProps): JSX.Element => {
    const cachedData = useCachedData();
    const { risk_id } = useParams<keyof UrlParams>();

    const [submitState, setSubmitState] = useState(SubmitState.None);
    const [displayedModal, setDisplayedModal] = useState(Modal.None);

    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [reference, setReference] = useState<string>();
    const [riskCategory, setRiskCategory] = useState<string>();
    const [riskStrategy, setRiskStrategy] = useState<string>();
    const [tags, setTags] = useState<string[]>([]);
    const [owner, setOwner] = useState<UserResponse>();
    const [treatmentPlan, setTreatmentPlan] = useState<string>();
    const [toastErrorMessage, setToastErrorMessage] = useState<string>();
    const [toastSuccess, setToastSuccess] = useState<{ text: string; link: string; linkText: string }>();
    const [initialRisk, setInitialRisk] = useState<RiskResponse>();
    const [riskCategoryOptions, setRiskCategoryOptions] = useState<RiskCategoryResponse[]>();
    const [riskStrategyOptions, setRiskStrategyOptions] = useState<RiskStrategy[]>();
    const descriptionAi = useAiContentAdvisor(props.artificialIntelligenceApi, description, AiContentAdvisorFeature.RISK_MANAGEMENT_RISK_DESCRIPTION, setToastErrorMessage);
    const treatmentPlanAi = useAiContentAdvisor(props.artificialIntelligenceApi, treatmentPlan ?? '', AiContentAdvisorFeature.RISK_MANAGEMENT_RISK_TREATMENT_PLAN_DESCRIPTION, setToastErrorMessage);

    const [pageErrorMessage, setPageErrorMessage] = useState<string>();

    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const controlIdQueryParam = query.get('controlId');

    const [actions, setActions] = useState<Action[]>();
    const [actionStatusFilter, setActionStatusFilter] = useState<ActionStatusFilterValues>(ActionStatusFilterValues.HIDE_CLOSED_ACTIONS);
    const [mappedActionIds, setMappedActionIds] = useState<string[]>();
    const [controlMappingItems, controlMappingItemsError, controlNavigatedFrom] = useControlMappingItems(props.controlsApi, controlIdQueryParam ? controlIdQueryParam : undefined);
    const [selectedControls, setSelectedControls] = useState<SelectedControl[]>();

    const MITIGATING_CONTROLS_ACCORDION = 'mitigatingControls';
    const TREATMENT_PLAN_ACCORDION = 'treatmentPlan';

    // Maintain a cache of controls indexed by identifier, so that names of controls can be retrieved quickly for children that need them.
    const controlsByIdentifier: Map<string, OperationalControl> | undefined = useMemo(() => {
        if (controlMappingItems === undefined) {
            return undefined;
        }

        return new Map(controlMappingItems.map((item) => [item.identifier, item]));
    }, [controlMappingItems]);

    // Sets the initial `selectedControls`. There are three cases for the initial value:
    // 1. The user navigated to this page from a Control Details page. A new risk is being created, and the control navigated from is the single selected control.
    // 2. The user is managing an existing Risk. The selected controls are the controls (if any) that are already mapped to the Risk.
    // 3. The user is creating a new Risk. No controls are selected.
    useEffect(() => {
        if (controlsByIdentifier === undefined) {
            return;
        }

        if (controlNavigatedFrom) {
            setSelectedControls([selectedControlFromOperationalControl(controlNavigatedFrom, 100)]);
        } else if (initialRisk) {
            const newSelectedControls: SelectedControl[] = [];
            let someControlNotFound = false;
            initialRisk.controls.forEach((controlMapping) => {
                const controlIdentifier = controlMapping.control.identifier;
                const controlMappingItem = controlsByIdentifier.get(controlIdentifier);
                if (controlMappingItem === undefined) {
                    someControlNotFound = true;
                    return;
                } else {
                    newSelectedControls.push(selectedControlFromOperationalControl(controlMappingItem, controlMapping.weight));
                }
            });

            if (someControlNotFound) {
                setPageErrorMessage(GENERIC_ERROR_MESSAGE);
            } else {
                setSelectedControls(newSelectedControls);
            }
        } else {
            setSelectedControls([]);
        }
    }, [controlsByIdentifier, controlNavigatedFrom, initialRisk]);

    useEffect(() => {
        const getRiskCategories = async (): Promise<void> => {
            try {
                const riskCategoryResponse = await props.riskRegisterApi.getRiskCategories();
                setRiskCategoryOptions(riskCategoryResponse.data);
            } catch (error) {
                setPageErrorMessage(error.message);
            }
        };
        getRiskCategories();
    }, [props.riskRegisterApi]);

    useEffect(() => {
        const getRiskStrategies = async (): Promise<void> => {
            try {
                const riskCategoryResponse = await props.riskRegisterApi.getRiskStrategies();
                setRiskStrategyOptions(riskCategoryResponse.data);
            } catch (error) {
                setPageErrorMessage(error.message);
            }
        };

        getRiskStrategies();
    }, [props.riskRegisterApi]);

    useEffect(() => {
        const getActions = async (): Promise<void> => {
            try {
                const response = await props.actionsApi.getAllActions();
                setActions(response.data);
            } catch (error) {
                setPageErrorMessage(error.message);
            }
        };
        getActions();
    }, [props.actionsApi]);

    useEffect(() => {
        const getInitialVersion = async () => {
            if (risk_id !== undefined) {
                try {
                    const riskDetailResponse = await props.riskRegisterApi.getRiskDetails(risk_id);
                    setInitialRisk(riskDetailResponse.data);

                    setTitle(riskDetailResponse.data.title);
                    setDescription(riskDetailResponse.data.description);
                    setReference(riskDetailResponse.data.reference);
                    setRiskCategory(riskDetailResponse.data.category.id);
                    setRiskStrategy(riskDetailResponse.data.strategy?.id);
                    setTags(riskDetailResponse.data.tags);
                    setOwner(cachedData.users.find((user) => user.cognito_subject === riskDetailResponse.data.owner));
                    setTreatmentPlan(riskDetailResponse.data.treatment_plan_description);
                } catch (error) {
                    setPageErrorMessage(error.message);
                }
            }
        };
        getInitialVersion();
    }, [props.riskRegisterApi, risk_id, cachedData.users]);

    useEffect(() => {
        if (props.pageType === RiskPageType.CREATE_NEW) {
            setMappedActionIds([]);
        } else if (props.pageType === RiskPageType.MANAGE && initialRisk) {
            setMappedActionIds(initialRisk.associated_actions.map((action) => action.id));
        }
    }, [props.pageType, initialRisk]);

    const multipleControlMappingProps: MultipleControlMappingProps | undefined = useMemo(
        () =>
            selectedControls && controlMappingItems && controlsByIdentifier
                ? {
                      controls: controlMappingItems,
                      handleControlChange: (controls: string[]) => {
                          const currentWeights = new Map(selectedControls.map((control) => [control.identifier, control.weight]));

                          setSelectedControls(
                              // For any control that is newly added, give it a weight of 0%.
                              controls.map((controlIdentifier) => selectedControlFromOperationalControl(controlsByIdentifier.get(controlIdentifier)!, currentWeights.get(controlIdentifier) ?? 0))
                          );
                      },
                      currentMappedControlIdentifiers: selectedControls.map((control) => control.identifier),
                  }
                : undefined,
        [controlMappingItems, controlsByIdentifier, selectedControls]
    );

    /**
     * `mappedActionIds` is the source of truth for which actions are mapped, but `MultipleActionMapping` only knows about the actions that pass the status filter.
     * So the mapped IDs given to it are limited to visible actions, and when it reports changes, mapped actions that are hidden by the filter are kept; otherwise, moving any action while closed actions are hidden would unmap every closed action.
     */
    const multipleActionMappingProps: MultipleActionMappingProps | undefined = useMemo(() => {
        if (actions === undefined || mappedActionIds === undefined) {
            return undefined;
        }

        const visibleActions = actions.filter((action) => actionStatusFilter === ActionStatusFilterValues.SHOW_CLOSED_ACTIONS || action.status !== ActionStatus.CLOSED);
        const visibleActionIds = new Set(visibleActions.map((action) => action.id));

        return {
            actions: visibleActions,
            handleChange: (actionIds: string[]) => setMappedActionIds([...mappedActionIds.filter((actionId) => !visibleActionIds.has(actionId)), ...actionIds]),
            currentlyMappedActionIds: mappedActionIds.filter((actionId) => visibleActionIds.has(actionId)),
        };
    }, [actions, actionStatusFilter, mappedActionIds]);

    const createBreadcrumb = (): JSX.Element => {
        if (controlNavigatedFrom) {
            const { controlFramework, controlGroupId, controlId } = getFrameworkGroupControlParts(controlNavigatedFrom);
            return (
                <Breadcrumb textColor="blue">
                    <BreadcrumbLink link={getFrameworkGroupControlURL(controlFramework)}>{controlFramework}</BreadcrumbLink>
                    <BreadcrumbLink link={getFrameworkGroupControlURL(`${controlFramework}#${controlGroupId}`)}>{controlNavigatedFrom.metadata.control_group_name}</BreadcrumbLink>
                    <BreadcrumbLink link={`${getFrameworkGroupControlURL(controlNavigatedFrom.identifier)}#risks`}>{controlNavigatedFrom.metadata.is_custom ? controlNavigatedFrom.metadata.control_name : controlId}</BreadcrumbLink>
                    <BreadcrumbText>Create New Risk</BreadcrumbText>
                </Breadcrumb>
            );
        } else if (risk_id && props.pageType === RiskPageType.MANAGE && initialRisk) {
            return (
                <Breadcrumb textColor="blue">
                    <BreadcrumbLink link={generatePath(RISK_LISTING)}>Risk Listing</BreadcrumbLink>
                    {submitState === SubmitState.Deleted ? <BreadcrumbText>{initialRisk.title}</BreadcrumbText> : <BreadcrumbLink link={generatePath(RISK_DETAILS, { risk_id: risk_id })}>{initialRisk.title}</BreadcrumbLink>}
                    <BreadcrumbText>Manage Risk</BreadcrumbText>
                </Breadcrumb>
            );
        } else
            return (
                <Breadcrumb textColor="blue">
                    <BreadcrumbLink link={generatePath(RISK_LISTING)}>Risk Listing</BreadcrumbLink>
                    <BreadcrumbText>Create New Risk</BreadcrumbText>
                </Breadcrumb>
            );
    };

    const deleteRisk = async (): Promise<string> => {
        await props.riskRegisterApi.deleteRisk(risk_id!);
        setSubmitState(SubmitState.Deleted);
        return 'Risk deleted.';
    };

    if (controlMappingItemsError) {
        return <Text>{controlMappingItemsError.message}</Text>;
    }

    if (pageErrorMessage) {
        return <Text>{pageErrorMessage}</Text>;
    }

    if (riskCategoryOptions && riskCategoryOptions.length < 1) {
        return <Text>There are no categories created for Risks. To continue, please create a category in Settings.</Text>;
    }

    if (multipleControlMappingProps && multipleActionMappingProps && selectedControls && mappedActionIds && riskStrategyOptions && riskCategoryOptions && controlMappingItems !== undefined && controlsByIdentifier !== undefined && ((props.pageType === RiskPageType.MANAGE && initialRisk) || props.pageType === RiskPageType.CREATE_NEW)) {
        const totalWeight = selectedControls.map((control) => control.weight).reduce((a, b) => a + b, 0);
        const distributionWarningMessage = (() => {
            if (totalWeight > 100) {
                return 'The total control distribution cannot be greater than 100%.';
            } else if (totalWeight < 100) {
                return WARNING_MESSAGE_INCOMPLETE_CONTROL_DISTRIBUTION;
            } else {
                return undefined;
            }
        })();

        const handleControlWeightsChanged = (weights: number[]) => {
            setSelectedControls(selectedControls.map((selectedControl, index) => ({ ...selectedControl, weight: weights[index] })));
        };

        const validateFormInput = () => {
            if (title.length === 0) {
                throw new ValidationError('Title is required.');
            }

            if (description.length === 0) {
                throw new ValidationError('Description is required.');
            }

            if (owner === undefined) {
                throw new ValidationError('Owner is required.');
            }

            if (riskCategory === undefined) {
                throw new ValidationError('Risk Category is required.');
            }

            if (totalWeight > 100) {
                throw new ValidationError('The total control distribution cannot be greater than 100%.');
            }
        };

        const createNewRisk = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
            event.preventDefault();

            setSubmitState(SubmitState.Saving);
            try {
                validateFormInput();
                const selectedCategory = riskCategoryOptions.find((category) => category.id === riskCategory);
                if (owner && description && selectedCategory && mappedActionIds) {
                    const saveRiskRequest: SaveRiskRequest = {
                        category: selectedCategory.id,
                        description: description,
                        tags: tags,
                        title: title,
                        owner: owner.cognito_subject,
                        strategy: riskStrategy,
                        reference: reference,
                        treatment_plan_description: treatmentPlan,
                        associated_actions: mappedActionIds,
                        controls: selectedControls.map((control) => ({ control: control.identifier, weight: control.weight })),
                    };
                    if (props.pageType === RiskPageType.MANAGE && risk_id) {
                        await props.riskRegisterApi.updateRisk(risk_id, saveRiskRequest);
                        // An existing Risk was updated. Currently, this can only be done from the Risk details page.
                        setToastSuccess({ text: 'Risk saved.', link: generatePath(RISK_DETAILS, { risk_id: risk_id }), linkText: 'View Risk' });
                    } else {
                        const createdRiskId = (await props.riskRegisterApi.createRisk(saveRiskRequest)).data;
                        if (controlNavigatedFrom) {
                            // A new Risk was created from the Control details page.
                            setToastSuccess({ text: 'Risk saved.', link: `${getFrameworkGroupControlURL(controlNavigatedFrom.identifier)}#risks`, linkText: 'Return to control' });
                        } else {
                            // A new Risk was created from the Risk listing page.
                            setToastSuccess({ text: 'Risk saved.', link: generatePath(RISK_DETAILS, { risk_id: createdRiskId }), linkText: 'View Risk' });
                        }
                    }
                    setSubmitState(SubmitState.Saved);
                }
            } catch (error) {
                setToastErrorMessage(error.message);
                setSubmitState(SubmitState.None);
            }
        };

        const formDisabled = submitState === SubmitState.Saved || submitState === SubmitState.Deleted;

        return (
            <>
                {displayedModal === Modal.Delete && initialRisk && (
                    <ConfirmationModal operationType={ConfirmationModalOperationType.DELETE_AFTER_TYPING_TO_CONFIRM} headerText="Delete Risk" areYouSureText={`Are you sure you want to delete "${initialRisk.title}"?`} performOperation={deleteRisk} hideModal={() => setDisplayedModal(Modal.None)}>
                        <ul className={styles.deleteConfirmationList}>
                            <li>
                                <Text>The risk will be unmapped from any controls that are mitigating it.</Text>
                            </li>
                            <li>
                                <Text>If the risk's treatment plan includes any actions, they will be unmapped from the treatment plan.</Text>
                            </li>
                        </ul>
                    </ConfirmationModal>
                )}
                {toastSuccess && <LinkButtonToast variant="success" clearToast={() => setToastSuccess(undefined)} text={toastSuccess.text} linkButtonText={toastSuccess.linkText} linkButtonTo={toastSuccess.link} />}
                {toastErrorMessage && <TextToast variant="failure" clearToast={() => setToastErrorMessage(undefined)} text={toastErrorMessage} />}
                <PageLayoutDefault
                    headerBreadcrumb={createBreadcrumb()}
                    headerTitle={props.pageType === RiskPageType.CREATE_NEW ? 'Create New Risk' : 'Manage Risk'}
                    body={[
                        {
                            title: 'Risk Details',
                            content: (
                                <Box component="form" onSubmit={createNewRisk} id="manageRisk" noValidate>
                                    <fieldset disabled={formDisabled}>
                                        <div className={styles.formFieldsContainer}>
                                            <div className={styles.fieldContainer}>
                                                <FormFieldText formFieldLabel="Title" formFieldId="title" required handleChange={(event: React.FormEvent<HTMLInputElement>) => setTitle(event.currentTarget.value)} value={title} />
                                            </div>
                                            <div className={styles.fieldContainer}>
                                                <FormFieldTextAreaAi formFieldLabel="Description" formFieldId="description" required handleChange={(event: React.FormEvent<HTMLInputElement>) => setDescription(event.currentTarget.value)} value={description} aiContentAdvisorValues={descriptionAi} />
                                            </div>
                                            <div className={styles.fieldGroupContainer}>
                                                <div className={styles.fieldContainer}>
                                                    <FormFieldSelect formFieldLabel="Risk Category" required formFieldId="risk_category" handleChange={(value: ChangeEventType) => setRiskCategory(value as string)} options={riskCategoryOptions.sort((categoryA, categoryB) => (categoryA.title > categoryB.title ? 1 : -1)).map((option) => ({ label: option.title, value: option.id }))} selectedOption={riskCategory} tooltip="The classification/grouping to which the risk belongs. Risk categories are defined in Settings." disabled={formDisabled} />
                                                </div>
                                                <div className={styles.fieldContainer}>
                                                    <FormFieldSelect formFieldLabel="Risk Strategy" formFieldId="risk_strategy" handleChange={(value: ChangeEventType) => setRiskStrategy(value !== undefined ? (value as string) : undefined)} options={riskStrategyOptions.sort((strategyA, strategyB) => (strategyA.title > strategyB.title ? 1 : -1)).map((option) => ({ label: option.title, value: option.id }))} selectedOption={riskStrategy} tooltip="How your organization will address the risk. Risk strategies are defined in Settings." disabled={formDisabled} />
                                                </div>
                                            </div>
                                            <div className={styles.fieldGroupContainer}>
                                                <div className={styles.fieldContainer}>
                                                    <FormFieldMultiOptionSelect
                                                        defaultSelectedOptions={cachedData
                                                            .getTagCategoriesGroupedOptions()
                                                            .map((group) => group.options)
                                                            .flat()
                                                            .filter((option) => tags.includes(option.value as string))}
                                                        formFieldLabel="Tags"
                                                        formFieldId="risk_tags"
                                                        handleChange={(value: GroupOptionType[]) => setTags(value.map((tag) => tag.value as string))}
                                                        options={cachedData.getTagCategoriesGroupedOptions()}
                                                        accessibilityLabel="tag selection"
                                                        disabled={formDisabled}
                                                    />
                                                </div>
                                                <div className={styles.fieldContainer}>
                                                    <FormFieldUserSelect rolesRestriction={RISK_ROLES} onUserSelected={(user?: UserResponse) => setOwner(user)} formFieldId="owner" selectedUser={owner} formFieldLabel="Owner" required tooltip="The individual responsible for the risk. Only users with access to Risk Management can own risks." disabled={formDisabled} />
                                                </div>
                                            </div>
                                            <div className={styles.fieldContainer}>
                                                <FormFieldText formFieldLabel="Reference" formFieldId="reference" handleChange={(event: React.FormEvent<HTMLInputElement>) => setReference(event.currentTarget.value)} value={reference ?? ''} tooltip={REFERENCE} />
                                            </div>
                                        </div>
                                        <div className={styles.accordionMargin}>
                                            <Accordion defaultActiveKey={initialRisk && selectedControls.length > 0 ? MITIGATING_CONTROLS_ACCORDION : undefined}>
                                                <div className={styles.accordionContainer}>
                                                    <AccordionToggle eventKey={MITIGATING_CONTROLS_ACCORDION} ariaLabelSuffix={'Map Mitigating Controls'} />
                                                    <div className={styles.accordionHeaderContainer}>
                                                        <Text variant="Header2">Map Mitigating Controls</Text>
                                                    </div>
                                                </div>
                                                <hr />
                                                <AccordionCollapse eventKey={MITIGATING_CONTROLS_ACCORDION}>
                                                    <div className={styles.accordionCollapse}>
                                                        <MultipleControlMapping {...multipleControlMappingProps} />
                                                        <div className={styles.controlDistribution}>
                                                            <Text variant="Header3">Control Distribution</Text>
                                                            {selectedControls.length > 0 ? <ControlDistribution controls={selectedControls} totalWeight={totalWeight} warningMessage={distributionWarningMessage} changeWeights={handleControlWeightsChanged} /> : <Text>No controls have been selected.</Text>}
                                                        </div>
                                                    </div>
                                                </AccordionCollapse>
                                            </Accordion>
                                        </div>
                                        <div className={styles.accordionMargin}>
                                            <Accordion defaultActiveKey={initialRisk && mappedActionIds.length > 0 ? TREATMENT_PLAN_ACCORDION : undefined}>
                                                <div className={styles.accordionContainer}>
                                                    <AccordionToggle eventKey={TREATMENT_PLAN_ACCORDION} ariaLabelSuffix={'Treatment Plan'} />
                                                    <div className={styles.accordionHeaderContainer}>
                                                        <Text variant="Header2">Treatment Plan</Text>
                                                    </div>
                                                </div>
                                                <hr />
                                                <AccordionCollapse eventKey={TREATMENT_PLAN_ACCORDION}>
                                                    <div className={styles.accordionCollapse}>
                                                        <div className={styles.fieldContainer}>
                                                            <FormFieldTextAreaAi formFieldLabel="Treatment Plan Description" formFieldId="treatment_plan_description" handleChange={(event: React.FormEvent<HTMLInputElement>) => setTreatmentPlan(event.currentTarget.value)} value={treatmentPlan} aiContentAdvisorValues={treatmentPlanAi} />
                                                        </div>
                                                        <div className={styles.spaceBetweenContainer}>
                                                            <Text noStyles variant="Header3">
                                                                Map Actions
                                                            </Text>
                                                            <div className={styles.fieldGroupContainer}>
                                                                <FormFieldSelect
                                                                    formFieldId="statusFilter"
                                                                    handleChange={(value: ChangeEventType): void => {
                                                                        setActionStatusFilter(value as ActionStatusFilterValues);
                                                                    }}
                                                                    notClearable
                                                                    options={ActionStatusFilterOptions}
                                                                    selectedOption={actionStatusFilter}
                                                                    disabled={formDisabled}
                                                                />
                                                                <Link openInNewTab size="sm" to={CREATE_ACTION} variant="link">
                                                                    Create Action
                                                                </Link>
                                                            </div>
                                                        </div>
                                                        <MultipleActionMapping {...multipleActionMappingProps} />
                                                    </div>
                                                </AccordionCollapse>
                                            </Accordion>
                                        </div>
                                        <div className={styles.spaceBetweenContainer}>
                                            {props.pageType === RiskPageType.MANAGE && (
                                                <Button variant="danger" disabled={submitState !== SubmitState.None} onClick={() => setDisplayedModal(Modal.Delete)} fontAwesomeImage={ICON_DELETE_REMOVE}>
                                                    Delete
                                                </Button>
                                            )}
                                            <div className={styles.submitButton}>
                                                <Button variant="submit" form="manageRisk" disabled={submitState !== SubmitState.None} isLoading={submitState === SubmitState.Saving} loadingText="Submitting...">
                                                    Submit
                                                </Button>
                                            </div>
                                        </div>
                                    </fieldset>
                                </Box>
                            ),
                        },
                    ]}
                />
            </>
        );
    } else return <Placeholder />;
};
